/**
 * MapLegendModal Component
 *
 * Full-screen modal explaining map symbols and indicators.
 * Helps users understand the visual language of the climbing map.
 */

import { useState, useEffect, type CSSProperties } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import HistoryIcon from '@mui/icons-material/History';
import StarIcon from '@mui/icons-material/Star';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MapIcon from '@mui/icons-material/Map';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import type { ColorGrade } from '@flash/shared';

export interface MapLegendModalProps {
  /** Called when modal is closed */
  onClose: () => void;
  /** Optional gym-specific color grades to display at the top */
  colorGrades?: ColorGrade[];
}

/**
 * Format grade display based on association type
 */
const formatGradeDisplay = (colorGrade: ColorGrade): string | null => {
  if (colorGrade.associationType === 'none') {
    return null;
  }
  if (colorGrade.associationType === 'exact') {
    return colorGrade.exactGrade;
  }
  if (colorGrade.associationType === 'range') {
    return `${colorGrade.minGrade}-${colorGrade.maxGrade}`;
  }
  return null;
};

export const MapLegendModal = ({ onClose, colorGrades }: MapLegendModalProps) => {
  const [isVisible, setIsVisible] = useState(false);

  // Animate in on mount
  useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timeout);
  }, []);

  // Handle close with animation
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          ...backdropStyle,
          opacity: isVisible ? 1 : 0,
        }}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        style={{
          ...modalStyle,
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Map Legend"
      >
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>Map Legend</h2>
          <button onClick={handleClose} style={closeButtonStyle} aria-label="Close legend">
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Gym-Specific Color Grades Section */}
          {colorGrades && colorGrades.length > 0 && (
            <section style={sectionStyle}>
              <h3 style={sectionTitleStyle}>This Gym&apos;s Colors</h3>
              {colorGrades.map((cg, index) => {
                const gradeText = formatGradeDisplay(cg);
                return (
                  <div key={index} style={legendItemStyle}>
                    <div style={colorGradeItemStyle}>
                      <div style={{ ...colorGradeDotStyle, backgroundColor: cg.color }} />
                      <span style={colorGradeNameStyle}>{cg.displayName}</span>
                    </div>
                    {gradeText && <span style={colorGradeValueStyle}>{gradeText}</span>}
                  </div>
                );
              })}
            </section>
          )}

          {/* Pin Colors Section */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Route Pins</h3>
            <div style={legendItemStyle}>
              <div style={pinExampleStyle}>
                <div style={{ ...pinDotStyle, backgroundColor: '#ef4444' }} />
              </div>
              <span style={legendTextStyle}>Main circle shows route hold color</span>
            </div>
            <div style={legendItemStyle}>
              <div style={pinExampleStyle}>
                <div style={pinDotWithTagStyle}>
                  <div style={{ ...pinDotStyle, backgroundColor: '#22c55e' }} />
                  <div style={{ ...tagDotStyle, backgroundColor: '#3b82f6' }} />
                </div>
              </div>
              <span style={legendTextStyle}>Small dot shows tag color (if different)</span>
            </div>
          </section>

          {/* Climb Indicators Section */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Status Indicators</h3>
            <div style={legendItemStyle}>
              <div style={iconContainerStyle}>
                <CheckCircleIcon style={{ color: '#22c55e', fontSize: 24 }} />
              </div>
              <span style={legendTextStyle}>Ticked / Sent</span>
            </div>
            <div style={legendItemStyle}>
              <div style={iconContainerStyle}>
                <FlashOnIcon style={{ color: '#f59e0b', fontSize: 24 }} />
              </div>
              <span style={legendTextStyle}>Flashed (sent first try)</span>
            </div>
            <div style={legendItemStyle}>
              <div style={iconContainerStyle}>
                <HistoryIcon style={{ color: '#6b7280', fontSize: 24 }} />
              </div>
              <span style={legendTextStyle}>Completed last month</span>
            </div>
            <div style={legendItemStyle}>
              <div style={iconContainerStyle}>
                <StarIcon style={{ color: '#8b5cf6', fontSize: 24 }} />
              </div>
              <span style={legendTextStyle}>Project (working on it)</span>
            </div>
          </section>

          {/* Grade Arrows Section */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Grade Modifiers</h3>
            <div style={legendItemStyle}>
              <div style={iconContainerStyle}>
                <ArrowDropUpIcon style={{ color: '#ef4444', fontSize: 28 }} />
              </div>
              <span style={legendTextStyle}>Harder than grade (e.g. V4+)</span>
            </div>
            <div style={legendItemStyle}>
              <div style={iconContainerStyle}>
                <ArrowDropDownIcon style={{ color: '#22c55e', fontSize: 28 }} />
              </div>
              <span style={legendTextStyle}>Easier than grade (e.g. V4-)</span>
            </div>
          </section>

          {/* View Toggles Section */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>View Toggles</h3>
            <div style={legendItemStyle}>
              <div style={iconContainerStyle}>
                <MapIcon style={{ color: 'var(--color-text-primary)', fontSize: 24 }} />
              </div>
              <span style={legendTextStyle}>Switch to map view</span>
            </div>
            <div style={legendItemStyle}>
              <div style={iconContainerStyle}>
                <FormatListBulletedIcon
                  style={{ color: 'var(--color-text-primary)', fontSize: 24 }}
                />
              </div>
              <span style={legendTextStyle}>Switch to list view</span>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

// Styles
const backdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  transition: 'opacity 300ms ease-out',
  zIndex: 1099,
};

const modalStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'var(--color-bg-primary, white)',
  transition: 'transform 300ms ease-out',
  zIndex: 1100,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid var(--color-border, #e5e7eb)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const closeButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  color: 'var(--color-text-primary, #1f2937)',
  cursor: 'pointer',
};

const contentStyle: CSSProperties = {
  flex: 1,
  padding: '20px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

const sectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const sectionTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary, #6b7280)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const legendItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
};

const iconContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  flexShrink: 0,
};

const pinExampleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  flexShrink: 0,
};

const pinDotStyle: CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: '50%',
  border: '3px solid white',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
};

const pinDotWithTagStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const tagDotStyle: CSSProperties = {
  position: 'absolute',
  bottom: -2,
  right: -2,
  width: 10,
  height: 10,
  borderRadius: '50%',
  border: '2px solid white',
};

const legendTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-primary, #1f2937)',
};

// Gym-specific color grades styles
const colorGradeItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flex: 1,
};

const colorGradeDotStyle: CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: '2px solid white',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  flexShrink: 0,
};

const colorGradeNameStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-primary, #1f2937)',
};

const colorGradeValueStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
  fontWeight: 500,
};

export default MapLegendModal;
